import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../hooks/authHelpers';
import { trackEvent } from '../analytics';

const useLeague = () => {
    const [currentLeague, setCurrentLeague] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allLeagues, setAllLeagues] = useState([]);
    const [pagination, setPagination] = useState(null);

    // Fetch current user's league
    const fetchCurrentUserLeague = async () => {
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/my-league`, 'GET');

            if (!response.ok) {
                if (response.status === 404) {
                    // User is not in any league
                    setCurrentLeague(null);
                    return { success: true, league: null, message: 'User is not in any league' };
                }
                if (response.status === 401) {
                    // User is not authorized or it's a guest
                    setCurrentLeague(null);
                    return { success: true, league: null, message: 'Log in to use league features' };
                }
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch league');
            }

            const data = await response.json();
            setCurrentLeague(data.league);
            return { success: true, league: data.league };
        } catch (err) {
            setError(err.message);
            setCurrentLeague(null);
            return { success: false, error: err.message };
        } finally {
            setInitialLoading(false);
        }
    };

    // Automatically fetch current user's league on mount
    useEffect(() => {
        fetchCurrentUserLeague();
    }, []);

    // Fetch league by ID
    const fetchLeagueById = async (leagueId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}`, 'GET');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch league');
            }

            const data = await response.json();
            return { success: true, league: data.league };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Fetch all leagues with pagination and search
    const fetchAllLeagues = async (page = 1, limit = 10, search = '') => {
        setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            if (search) {
                queryParams.append('search', search);
            }

            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league?${queryParams}`, 'GET');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch leagues');
            }

            const data = await response.json();
            setAllLeagues(data.leagues);
            setPagination(data.pagination);
            return { success: true, leagues: data.leagues, pagination: data.pagination };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Create a new league
    const createLeague = async ({ name, description = '' }) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league`, 'POST', { name, description });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create league');
            }

            const data = await response.json();
            setCurrentLeague(data.league);
            return { success: true, league: data.league, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Join a league
    const joinLeague = async (leagueId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/join`, 'POST');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to join league');
            }

            const data = await response.json();
            setCurrentLeague(data.league);
            trackEvent('league_joined', { league_id: leagueId });
            return { success: true, league: data.league, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Get league info by invite code
    const getLeagueByInviteCode = async (inviteCode) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/info/${inviteCode}`, 'GET');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch league info');
            }

            const data = await response.json();
            return { success: true, league: data.league };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Join a league with an invite code
    const joinLeagueWithCode = async (inviteCode) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/join/${inviteCode}`, 'POST');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to join league');
            }

            const data = await response.json();
            setCurrentLeague(data.league);
            trackEvent('league_joined', { league_id: data.league?._id, join_method: 'invite' });
            return { success: true, league: data.league, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Get league invite code (owner only)
    const getLeagueInviteCode = async (leagueId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/invite-code`, 'GET');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to get invite code');
            }

            const data = await response.json();
            return { success: true, inviteCode: data.inviteCode };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Reset league invite code (owner only)
    const resetLeagueInviteCode = async (leagueId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/reset-invite-code`, 'PUT');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to reset invite code');
            }

            const data = await response.json();
            return { success: true, inviteCode: data.inviteCode, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Toggle league joinable status (owner only)
    const toggleIsJoinable = async (leagueId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/toggle-joinable`, 'PUT');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to toggle joinable status');
            }

            const data = await response.json();
            // Update the current league state
            setCurrentLeague(prevLeague => ({ ...prevLeague, isJoinable: data.isJoinable }));
            return { success: true, isJoinable: data.isJoinable, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Toggle league show inactive members status (owner only)
    const toggleShowInactiveMembers = async (leagueId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/toggle-show-inactive-members`, 'PUT');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to toggle show inactive members status');
            }

            const data = await response.json();
            // Update the current league state
            setCurrentLeague(prevLeague => ({ ...prevLeague, showInactiveMembers: data.showInactiveMembers }));
            return { success: true, showInactiveMembers: data.showInactiveMembers, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Leave current league
    const leaveLeague = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/leave`, 'POST');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to leave league');
            }

            const data = await response.json();
            setCurrentLeague(null);
            return { success: true, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Update league (owner only)
    // Update league (owner only)
    const updateLeague = async (leagueId, { name, description }) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}`, 'PUT', { name, description });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update league');
            }

            const data = await response.json();
            setCurrentLeague(data.league);
            return { success: true, league: data.league, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Delete league (owner only)
    const deleteLeague = async (leagueId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}`, 'DELETE');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to delete league');
            }

            const data = await response.json();
            setCurrentLeague(null);
            return { success: true, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Invite friend to league
    const inviteFriendToLeague = async (leagueId, friendId) => {
        // Remove global loading states since components handle their own
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/invite-friend`, 'POST', { friendId });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to invite friend');
            }

            const data = await response.json();
            return { success: true, message: data.message };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // Kick member from league
    const kickMember = async (leagueId, memberId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/kick-member`, 'POST', { memberId });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to kick member');
            }

            const data = await response.json();
            return { success: true, message: data.message };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // Ban member from league
    const banMember = async (leagueId, memberId, reason = '') => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/ban-member`, 'POST', { memberId, reason });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to ban member');
            }

            const data = await response.json();
            return { success: true, message: data.message };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // Unban member from league
    const unbanMember = async (leagueId, userId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/unban-member`, 'POST', { userId });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to unban member');
            }

            const data = await response.json();
            return { success: true, message: data.message };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // Transfer league ownership
    const transferOwnership = async (leagueId, newOwnerId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/transfer-ownership`, 'POST', { newOwnerId });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to transfer ownership');
            }

            const data = await response.json();
            return { success: true, message: data.message };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    // Set seasonal rewards
    const setSeasonalRewards = async (leagueId, seasonId, rewards) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/rewards/season/${seasonId}`, 'PUT', { rewards });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to set seasonal rewards');
            }

            const data = await response.json();
            setCurrentLeague(data.league);
            return { success: true, league: data.league, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Set event rewards
    const setEventRewards = async (leagueId, seasonId, eventId, rewards) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/rewards/event/${seasonId}/${eventId}`, 'PUT', { rewards });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to set event rewards');
            }

            const data = await response.json();
            setCurrentLeague(data.league);
            return { success: true, league: data.league, message: data.message };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Get league rewards for a season
    const getLeagueRewards = async (leagueId, seasonId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/rewards/${seasonId}`, 'GET');

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch league rewards');
            }

            const data = await response.json();
            return { success: true, seasonalRewards: data.seasonalRewards, eventRewards: data.eventRewards };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    // Clear error
    const clearError = () => {
        setError(null);
    };

    // Clear current league data
    const clearCurrentLeague = () => {
        setCurrentLeague(null);
    };

    return {
        // State
        currentLeague,
        allLeagues,
        pagination,
        loading,
        initialLoading,
        error,

        // Methods
        fetchCurrentUserLeague,
        fetchLeagueById,
        fetchAllLeagues,
        createLeague,
        joinLeague,
        leaveLeague,
        updateLeague,
        deleteLeague,
        clearError,
        clearCurrentLeague,
        joinLeagueWithCode,
        getLeagueInviteCode,
        resetLeagueInviteCode,
        toggleIsJoinable,
        toggleShowInactiveMembers,
        inviteFriendToLeague,
        getLeagueByInviteCode,
        kickMember,
        banMember,
        unbanMember,
        transferOwnership,
        setSeasonalRewards,
        setEventRewards,
        getLeagueRewards
    };
};

export default useLeague;
